import { useNavigate } from 'react-router-dom';
import { useStore } from '@/lib/store';
import { generate } from '@/lib/api';
import { FormProgress } from '@/components/form/FormProgress';
import { FormNavigation } from '@/components/form/FormNavigation';
import { StepOne } from '@/components/form/StepOne';
import { StepTwo } from '@/components/form/StepTwo';
import { StepThree } from '@/components/form/StepThree';
import { StepFour } from '@/components/form/StepFour';
import { StepFive } from '@/components/form/StepFive';
import { useState } from 'react';

export function GeneratePage() {
  const navigate = useNavigate();
  const {
    formData,
    nextStep,
    prevStep,
    setSessionId,
    setPreviews,
    setIsGenerating,
    isGenerating,
  } = useStore();
  const [error, setError] = useState<string | null>(null);

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(
          formData.businessName?.trim() &&
          formData.industry &&
          formData.description?.trim() &&
          formData.description.length >= 10
        );
      case 2:
        return !!(formData.style && formData.primaryColor && formData.secondaryColor);
      case 3:
        return !!(formData.sections && formData.sections.length > 0);
      case 4:
        return !!(formData.contactEmail && formData.contactEmail.includes('@'));
      case 5:
        return validateStep(1) && validateStep(2) && validateStep(3) && validateStep(4);
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;

    setIsGenerating(true);
    setError(null);

    try {
      const result = await generate.create({
        businessName: formData.businessName!,
        industry: formData.industry!,
        description: formData.description!,
        targetAudience: formData.targetAudience || '',
        primaryColor: formData.primaryColor!,
        secondaryColor: formData.secondaryColor!,
        style: formData.style!,
        sections: formData.sections!,
        contactEmail: formData.contactEmail!,
        customCopy: formData.customCopy,
      });

      setSessionId(result.sessionId);
      setPreviews(result.previews);
      navigate(`/preview/${result.sessionId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const renderStep = () => {
    switch (formData.step) {
      case 1:
        return <StepOne />;
      case 2:
        return <StepTwo />;
      case 3:
        return <StepThree />;
      case 4:
        return <StepFour />;
      case 5:
        return <StepFive />;
      default:
        return <StepOne />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Create Your Landing Page</h1>
          <p className="text-gray-600 mt-2">Fill out the form and we'll design your perfect page.</p>
        </div>

        <div className="card">
          <FormProgress currentStep={formData.step} totalSteps={5} />

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {renderStep()}

          <FormNavigation
            step={formData.step}
            totalSteps={5}
            onNext={nextStep}
            onBack={prevStep}
            onSubmit={handleSubmit}
            canProceed={validateStep(formData.step)}
            isSubmitting={isGenerating}
          />
        </div>
      </div>
    </div>
  );
}
